import { useFlag } from '@calendly/feature-check-react';
import { useFormContext, useFormField } from '@calendly/form';
import { FieldLabel } from '@calendly/form/parts';
import { I18n } from '@calendly/i18n';
import { FullStoryExclude } from '@calendly/ui/components/fullstory-privacy';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useState, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import ErrorMessage from 'components/core/error_message';
import { canUpdateLogoSelector, isProfileLogoSelector } from '../../../selectors';
import { LastEdited } from '../last_edited';
import { ControlBlock } from './control_block';
import { EmptyLogoBox, LogoBox } from './logo_box';
import { LogoTipImage } from './logo_tip_image';
import { Restricted } from './restricted';
const wrapperIsAdminIACss = "w1ozv7kr";
const wrapperCss = "ww70nxf";
const Wrapper = forwardRef((props, ref) => {
  const {
    isAdminIA,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, {
    [wrapperIsAdminIACss]: isAdminIA
  }, props.className)} />;
});
const containerIsAdminIACss = "c1au346f";
const containerCss = "c1cwuggs";
const Container = forwardRef((props, ref) => {
  const {
    isAdminIA,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsAdminIACss]: isAdminIA
  }, props.className)} />;
});
const Field = ({
  isAdminIA = false
}) => {
  const {
    setFieldValue,
    setFieldTouched,
    initialValues: {
      logo: initialLogoUrl
    }
  } = useFormContext();
  const [previewUrl, setPreviewUrl] = useState(initialLogoUrl);
  const [logoProps, meta, {
    setError
  }] = useFormField({
    name: 'logo'
  });
  const [cropDataProps] = useFormField({
    name: 'cropData'
  });
  const [removeLogoProps] = useFormField({
    name: 'removeLogo'
  });
  const onFileSelect = ({
    cropData,
    previewDataUrl,
    file
  }) => {
    setError(); // candidate file was accepted, reset inline errors
    setFieldValue('logo', file);
    setFieldTouched('logo');
    setFieldValue('cropData', cropData);
    setFieldTouched('cropData');
    setFieldValue('removeLogo', false);
    setFieldTouched('removeLogo');
    setPreviewUrl(previewDataUrl);
  };
  const onFileError = errorMessage => {
    setError(errorMessage);
    setFieldTouched('logo', false);
  };
  const onImageRemove = () => {
    setFieldValue('logo', false);
    setFieldValue('cropData', '');
    if (initialLogoUrl) {
      setFieldValue('removeLogo', true);
      setFieldTouched('removeLogo');
      setFieldTouched('logo', true);
      setFieldTouched('cropData', true);
    } else {
      setFieldTouched('logo', false);
      setFieldTouched('cropData', false);
    }
  };
  return <FullStoryExclude>
      <input type="hidden" {...{
      ...logoProps,
      value: logoProps.value ?? ''
    }} />
      <input type="hidden" {...cropDataProps} />
      <input type="hidden" {...removeLogoProps} />
      {logoProps.value ? <LogoBox src={previewUrl} /> : <EmptyLogoBox />}
      <ControlBlock imageExists={Boolean(logoProps.value)} onImageRemove={onImageRemove} onFileSelect={onFileSelect} onFileError={onFileError} isAdminIA={isAdminIA} />
      {isAdminIA && !meta.error ? null : <ErrorMessage message={meta.error} />}
    </FullStoryExclude>;
};
export const LogoField = ({
  children,
  isAdminIA = false
}) => {
  const isGroupBrandingFeatureEnabled = useFlag('org_enablement_group_branding_feature');
  const isProfileLogo = useSelector(isProfileLogoSelector);
  const canUpdateLogo = useSelector(canUpdateLogoSelector);
  const isDefaultUpdateLogoState = !isGroupBrandingFeatureEnabled && canUpdateLogo;
  const isBrandingFeatureUpdateLogoState = isGroupBrandingFeatureEnabled && canUpdateLogo && isProfileLogo;
  const LogoComponent = isDefaultUpdateLogoState || isBrandingFeatureUpdateLogoState ? Field : Restricted;
  return <Wrapper isAdminIA={isAdminIA}>
      {isAdminIA ? <BodyText>
          {I18n.t('publisher.admin.branding.org_logo_form.description')}
        </BodyText> : <>
          <FieldLabel text={I18n.t('publisher.account_settings.branding.logo')} proTip={<>
                {I18n.t('publisher.account_settings.branding.logo_tip.part1')}
                <LogoTipImage />
                {I18n.t('publisher.account_settings.branding.logo_tip.part2')}
              </>} />
          {isGroupBrandingFeatureEnabled ? <BodyText>
              {I18n.t('publisher.account_settings.branding.logo_field_description')}
            </BodyText> : <BodyText>
              {I18n.t('publisher.account_settings.branding.logo_description')}
            </BodyText>}
        </>}
      {!isAdminIA && !isGroupBrandingFeatureEnabled && isProfileLogo && <LastEdited />}
      {children && children}
      <Container isAdminIA={isAdminIA}>
        <LogoComponent isAdminIA={isAdminIA} />
      </Container>
    </Wrapper>;
};

require("./logo_field.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./logo_field.js");